import React from 'react';
import Select from 'components/shared/Select/Select';
import ChimeLogo from 'components/shared/ChimeLogo/ChimeLogo';
import { Colorways, CompareMobileDropdownVariants, type CompareMobileDropdownVariant } from 'types/constants';
import {
  DEFAULT_HIGHLIGHT_COLOR,
  isDarkColorway,
  isGradientColorway,
  MIN_COLS,
  COLS_TO_SKIP,
  INIT_COL_CLASS,
} from '../consts';
import { Node } from '@contentful/rich-text-types';
import { TableRow, SelectOption } from '../types';
import { getColorwayName } from 'utils/formatters';
import type { Color } from 'libs/contentful/types';
import { getColorway, getCustomBackgroundColor } from 'utils/styles';
import { TableWrapper } from './TableWrapper';

const COLUMN_SELECT_CLASS = 'column-select';

const getTbodyColorway = (colorway: { name: Colorways } | Colorways): string => {
  const color = getColorwayName(colorway);

  switch (color) {
    case Colorways.Secondary:
    case Colorways.Tertiary:
      return 'tbody-white';
    case Colorways.Inverse:
      return 'tbody-medium-green';
    default:
    case Colorways.Primary:
      return 'tbody-light-green';
  }
};
const switchColumn = (columnIndex: number, wrapperEl: HTMLElement) => {
  const selectedColumn = String(columnIndex);
  const thisTable = wrapperEl.closest(`.${COLUMN_SELECT_CLASS}`)?.nextElementSibling;
  thisTable?.querySelectorAll(`.${INIT_COL_CLASS}`).forEach((cell: Element) => {
    cell.classList.remove(INIT_COL_CLASS);
  });

  thisTable?.querySelectorAll(`[data-column]`).forEach((cell: Element) => {
    if (cell.getAttribute('data-column') === selectedColumn) {
      cell.classList.remove('col-hidden');
    } else if (parseInt(cell.getAttribute('data-column') ?? '0') > COLS_TO_SKIP - 1) {
      cell.classList.add('col-hidden');
    }
  });
};

export const renderTable = (
  _node: Node,
  children: React.ReactNode,
  color: Colorways,
  highlightedColumnIndex: number,
  highlightColor: Color | undefined,
  mobileDropdownVariant: CompareMobileDropdownVariant | undefined,
) => {
  const isBorderedDropdown = mobileDropdownVariant === CompareMobileDropdownVariants.BORDERED;
  const rows = React.Children.toArray(children) as TableRow[];
  const colCount = rows.filter(c => c.type === 'tr')[0].props.children[0].length;
  const moreThanMin = colCount > MIN_COLS;

  const selectOptions: SelectOption[] | null = moreThanMin
    ? rows[0].props.children[0].slice(COLS_TO_SKIP).map(option => ({
        key: (typeof option.props.children[0] === 'string'
          ? option.props.children[0]
          : option?.props?.children[0]?.props?.children)!,
      }))
    : null;

  const customBg = highlightColor ? getCustomBackgroundColor(highlightColor) : undefined;
  const highlightColorwayClass = getColorway(highlightColor ?? DEFAULT_HIGHLIGHT_COLOR);
  const highlightContent =
    customBg || highlightColor?.name === Colorways.Custom || isDarkColorway(highlightColorwayClass)
      ? 'inverse'
      : 'primary';

  return (
    <TableWrapper highlightedColumnIndex={highlightedColumnIndex} highlightColor={highlightColor}>
      {moreThanMin && (
        <div className={`${COLUMN_SELECT_CLASS} font-sans font-semibold mb-8 sm:hidden`}>
          {isBorderedDropdown ? (
            <div className="flex items-center gap-2 mb-4">
              <ChimeLogo color="chime_green" size="small" alt="Chime" />
              <span>compared with</span>
            </div>
          ) : (
            <>Chime vs&nbsp;</>
          )}
          <Select
            optionHeader="Compare to"
            selectOptions={selectOptions ?? []}
            onOptionClick={switchColumn}
            colsToSkip={COLS_TO_SKIP}
            bordered={isBorderedDropdown}
          />
        </div>
      )}
      <table
        className="rich-text-table"
        data-highlighted-column-index={highlightedColumnIndex}
        data-highlight-content={highlightContent}>
        <colgroup>
          {Array.from({ length: colCount }, (_, i) => {
            const isHighlighted = i === highlightedColumnIndex;
            const useColBg = isHighlighted && !isGradientColorway(highlightColorwayClass);
            return (
              <col
                key={i}
                {...(useColBg && (customBg ? { style: customBg } : { className: highlightColorwayClass }))}
              />
            );
          })}
        </colgroup>
        <tbody className={getTbodyColorway(color)}>{children}</tbody>
      </table>
    </TableWrapper>
  );
};
